import { useCallback, useEffect, useState } from "react";

import { getErrorMessage } from "@/core/services";
import { useInject } from "@/hooks";
import { HOME_GATEWAY } from "./home.gateway";
import type { HomeQueryState, IHomeGateway } from "./home.types";

export const HOME_ERROR_MESSAGE = "Não foi possível carregar as informações da Tela Inicial.";

const INITIAL_STATE: HomeQueryState = {
  status: "loading",
  summary: null,
  errorMessage: null,
};

interface UseHomeSummaryResult extends HomeQueryState {
  reload: () => void;
}

/**
 * Consulta única da Tela Inicial: um `getSummary` alimenta as duas visões e os
 * dois gráficos, de modo que alternar entre Visão Geral e Obras em Andamento não
 * dispara nova busca (AC6).
 *
 * O gateway vem do `Registry`, e não de um import direto: a troca do mock pela
 * implementação HTTP não toca neste hook.
 */
export function useHomeSummary(): UseHomeSummaryResult {
  const gateway = useInject<IHomeGateway>(HOME_GATEWAY);
  const [state, setState] = useState<HomeQueryState>(INITIAL_STATE);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    setState(INITIAL_STATE);

    gateway
      .getSummary({ signal: controller.signal })
      .then((summary) => {
        if (cancelled) return;
        setState({ status: "success", summary, errorMessage: null });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setState({
          status: "error",
          summary: null,
          errorMessage: getErrorMessage(error) || HOME_ERROR_MESSAGE,
        });
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [gateway, reloadToken]);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  return { ...state, reload };
}
